import { z } from 'zod';

/**
 * Validação de fronteira dos dois endpoints de PIX (T005, Constitution IV,
 * `contracts/erp-pix-api.md` §1/§2).
 *
 * Mesma convenção de `pagamento.schema.ts`/`produto.schema.ts`: `looseObject` —
 * o Checkout valida o que consome e deixa o resto da resposta íntegro
 * (Constitution III).
 *
 * Nenhum `double` de dinheiro atravessa esta fronteira: `TrnValor` só **sai**,
 * no corpo de `GerarPIX`, e as duas respostas trazem apenas strings. Por isso
 * não há `valorEmCentavos` aqui.
 */

/**
 * `GerarPIXOutput` (yaml linhas 733-740).
 *
 * Os três campos são obrigatórios e os dois base64 precisam ser **não-vazios**:
 * sem `Trnbase64image` não há QR Code para exibir e sem `Trnbase64text` não há
 * "copia e cola" — uma resposta sem eles não é uma cobrança parcial, é uma
 * cobrança que o operador não consegue apresentar ao cliente. Falhar na
 * fronteira leva ao toast de "Tentar novamente" (`research.md` D12), que é o
 * desfecho correto.
 *
 * O `.min(1)` só foi acrescentado em 2026-09-04 (AD-165), e o TSDoc acima já
 * dizia "obrigatórios" antes disso — `z.string()` sozinho não cumpria a
 * promessa. `GerarPIXOutput` é o **único** endpoint do contrato sem `messages`:
 * quando recusa a cobrança, o ERP responde `200` com
 * `{"TrnGUID":"00000000-0000-0000-0000-000000000000","Trnbase64text":"",
 * "Trnbase64image":""}` e nenhuma mensagem de erro (verificado ao vivo contra o
 * ERP real). Sem este piso, `paraCobrancaPix` montaria uma cobrança com QR Code
 * vazio e "copia e cola" vazio, e a janela de PIX abriria como se a cobrança
 * existisse — o operador esperaria indefinidamente por um pagamento que nunca
 * foi gerado.
 */
const GUID_NULO = '00000000-0000-0000-0000-000000000000';

export const gerarPixOutputSchema = z.looseObject({
  /**
   * Chave da cobrança, **gerada pelo ERP** (AD-251) — é com ela que o polling
   * de `StatusPIX` pergunta pelo pagamento.
   *
   * O piso é mais duro que o dos base64 porque o GUID nulo é um valor que o ERP
   * de fato devolve quando não processa: aceitá-lo produziria uma cobrança cuja
   * consulta de status sempre responderia "Transação não localizada", e a
   * janela fecharia sozinha sem ninguém entender por quê.
   */
  TrnGUID: z
    .string()
    .min(1)
    .refine((valor) => valor !== GUID_NULO, {
      message: 'TrnGUID nulo: o ERP não gerou a cobrança',
    }),
  Trnbase64text: z.string().min(1),
  Trnbase64image: z.string().min(1),
});

/**
 * `StatusPIXOutput` (yaml linhas 742-747).
 *
 * `StatusTransacao` é `string` **livre**, não a união fechada dos dez literais
 * de AD-102 (`research.md` D15). Um `z.enum` aqui rejeitaria um literal novo
 * cadastrado no ERP e derrubaria a tela no meio de uma cobrança em curso; quem
 * decide o significado é `interpretarStatusPix`, que tem o ramo explícito
 * "desconhecido → falha terminal, nunca aprovado" (invariante J2). É o mesmo
 * padrão de resiliência que a feature 008 aplica a `FormaMeioPagtoNFe`.
 *
 * `messages` é o envelope padrão do ERP e chega opcional: nenhum requisito desta
 * feature o consome — o motivo exibido ao operador vem do literal de status, que
 * é o campo que o ERP de fato preenche em todo desfecho.
 */
export const statusPixOutputSchema = z.looseObject({
  StatusTransacao: z.string(),
  messages: z.array(z.unknown()).optional(),
});

export type GerarPixOutput = z.infer<typeof gerarPixOutputSchema>;
export type StatusPixOutput = z.infer<typeof statusPixOutputSchema>;
